import { buildAuthHeaders } from "../forum/authHeaders";

const IS_LOCAL = typeof window !== "undefined" && ["localhost", "127.0.0.1"].includes(window.location.hostname);
const API_BASE = IS_LOCAL
  ? process.env.REACT_APP_API_BASE_URL_LOCAL || ""
  : process.env.REACT_APP_API_BASE_URL || "";

function url(path) {
  return API_BASE ? `${API_BASE}${path}` : path;
}

async function request(path, options = {}) {
  const response = await fetch(url(path), {
    ...options,
    credentials: "include",
    headers: buildAuthHeaders({ Accept: "application/json", ...(options.headers || {}) }),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const detail = payload?.detail || Object.values(payload || {}).find((value) => Array.isArray(value) && typeof value[0] === "string")?.[0];
    const error = new Error(typeof detail === "string" ? detail : "L’opération n’a pas pu être effectuée.");
    error.status = response.status;
    error.code = payload?.code || "request_failed";
    throw error;
  }
  return payload;
}

function idempotencyKey() {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `checkout-${Date.now()}-${Math.random().toString(36).slice(2)}-eternua`;
}

export function fetchMonetizationStatus({ signal } = {}) {
  return request("/api/monetization/status/", { signal });
}

export function resumeMarketplaceCheckout(orderId) {
  return request(`/api/monetization/marketplace/orders/${encodeURIComponent(orderId)}/resume/`, { method: "POST" });
}

export function fetchConnectFeeEstimate(service, grossMinor, commissionMinor, { signal } = {}) {
  const params = new URLSearchParams({
    service: String(service || ""),
    gross_minor: String(Math.max(0, Math.round(Number(grossMinor) || 0))),
    commission_minor: String(Math.max(0, Math.round(Number(commissionMinor) || 0))),
  });
  return request(`/api/monetization/connect-policy/?${params.toString()}`, { signal });
}

export function fetchMarketplaceQuote(listingId, { signal, shippingMethod = "", promoCode = "" } = {}) {
  const shipping = shippingMethod ? `&shipping_method=${encodeURIComponent(shippingMethod)}` : "";
  const promotion = promoCode ? `&promo_code=${encodeURIComponent(promoCode)}` : "";
  return request(
    `/api/monetization/marketplace/quote/?listing_id=${encodeURIComponent(String(listingId || ""))}${shipping}${promotion}`,
    { signal }
  );
}

export function fetchMonetizationCenter({ signal } = {}) {
  return request("/api/monetization/me/", { signal });
}

export function fetchFinancialTransparency({ signal } = {}) {
  return request("/api/monetization/transparency/", { signal });
}

export function financialTransparencyExportUrl(format) {
  return url(`/api/monetization/transparency/?export=${encodeURIComponent(format)}`);
}

export function requestFinancialDataAction(action, reason) {
  return request("/api/monetization/transparency/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action, reason }),
  });
}

export function updateCommercialPreferences(values) {
  return request("/api/monetization/preferences/", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(values),
  });
}

export function createMonetizationCheckout({ productCode, listingId }) {
  return request("/api/monetization/checkout/", {
    method: "POST",
    headers: { "Content-Type": "application/json", "Idempotency-Key": idempotencyKey() },
    body: JSON.stringify({
      product_code: productCode,
      listing_type: "accounts.market_sell_post",
      listing_id: String(listingId || ""),
    }),
  });
}

export function createMarketplaceCheckout(listingId, shippingMethod = "", acknowledgements = {}, proposalId = "", checkoutOptions = {}) {
  return request("/api/monetization/marketplace/checkout/", {
    method: "POST",
    headers: { "Content-Type": "application/json", "Idempotency-Key": checkoutOptions.idempotencyKey || idempotencyKey() },
    // Amounts remain server-authoritative; the browser only selects a method
    // already declared by the seller.
    body: JSON.stringify({
      listing_id: String(listingId || ""),
      shipping_method: String(shippingMethod || ""),
      checkout_group_id: String(checkoutOptions.checkoutGroupId || ""),
      ...(proposalId ? { proposal_id: String(proposalId) } : {}),
      promo_code: String(checkoutOptions.promoCode || ""),
      address_label: String(checkoutOptions.address?.address_label || ""),
      shipping_address_line: String(checkoutOptions.address?.address_line || ""),
      shipping_city: String(checkoutOptions.address?.city || ""),
      shipping_postal_code: String(checkoutOptions.address?.postal_code || ""),
      shipping_country: String(checkoutOptions.address?.country || "France"),
      relay_point_id: String(checkoutOptions.address?.relay_point_id || ""),
      relay_point_label: String(checkoutOptions.address?.relay_point_label || ""),
      price_acknowledged: acknowledgements.price_acknowledged === true,
      deadline_acknowledged: acknowledgements.deadline_acknowledged === true,
      cancellation_acknowledged: acknowledgements.cancellation_acknowledged === true,
      refund_acknowledged: acknowledgements.refund_acknowledged === true,
    }),
  });
}

export function fetchSellerPaymentAccount({ signal } = {}) {
  return request("/api/monetization/marketplace/seller-account/", { signal });
}

export function fetchMarketplaceTraderProfile({ signal } = {}) {
  return request("/api/monetization/marketplace/trader-profile/", { signal });
}

export function updateMarketplaceTraderProfile(values) {
  return request("/api/monetization/marketplace/trader-profile/", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(values),
  });
}

export function syncSellerPaymentAccount() {
  return request("/api/monetization/marketplace/seller-account/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "sync" }),
  });
}

export function createSellerOnboardingLink() {
  return request("/api/monetization/marketplace/seller-account/onboarding/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
}

export function createSellerDashboardLink() {
  return request("/api/monetization/marketplace/seller-account/dashboard/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
}

export function fetchMarketplaceOrders(role = "all", { signal } = {}) {
  return request(`/api/monetization/marketplace/orders/?role=${encodeURIComponent(role)}`, { signal });
}

export function fetchCommercialWorkspace(kind, { page = 1, status = "", signal } = {}) {
  const params = new URLSearchParams({ page, status });
  return request(`/api/monetization/workspace/${kind}/?${params}`, { signal });
}
export function stopIncludedBoost(id) {
  return request(`/api/monetization/workspace/boosts/${encodeURIComponent(id)}/stop/`, { method: "POST" });
}
export function estimateCommission(values) {
  return request("/api/monetization/workspace/commissions/", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(values) });
}
export async function exportMyCommissions(status = "") {
  const response = await fetch(url(`/api/monetization/workspace/commissions/?export=csv&status=${encodeURIComponent(status)}`), { credentials: "include", headers: buildAuthHeaders({ Accept: "text/csv" }) });
  if (!response.ok) throw new Error("L’export a échoué.");
  const objectUrl = URL.createObjectURL(await response.blob());
  const anchor = document.createElement("a"); anchor.href = objectUrl; anchor.download = "mes-commissions.csv"; anchor.click();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

export function fetchBookingPayments(service, { signal } = {}) {
  return request(`/api/monetization/booking-payments/?service=${encodeURIComponent(service)}`, { signal });
}
export function bookingPaymentAction(id, action, body = {}) {
  const path = id ? `${encodeURIComponent(id)}/${encodeURIComponent(action)}/` : "";
  return request(`/api/monetization/booking-payments/${path}`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
}

export function bookingPaymentReview(id, body) {
  return request(`/api/monetization/booking-payments/${encodeURIComponent(id)}/review/`, body === undefined ? {} : {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
}

export function acceptGoodsRefundConditions(version) {
  return request("/api/monetization/marketplace/refund-conditions/accept/", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ version, accepted: true }) });
}

export function fundingStripeRequest(pilotId, paymentId, action, body) {
  const path = paymentId ? `${encodeURIComponent(paymentId)}/${encodeURIComponent(action)}/` : action ? `${encodeURIComponent(action)}/` : "";
  return request(`/api/monetization/funding-payments/${encodeURIComponent(pilotId)}/${path}`, body === undefined ? {} : {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
}

export function platformSupportRequest(paymentId, action, body) {
  const suffix = paymentId ? `${encodeURIComponent(paymentId)}/${encodeURIComponent(action)}/` : "";
  return request(`/api/monetization/platform-support/${suffix}`, body === undefined ? {} : {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
}

export async function downloadPlatformSupportDocument(paymentId, kind) {
  const response = await fetch(url(`/api/monetization/platform-support/${encodeURIComponent(paymentId)}/documents/${encodeURIComponent(kind)}/`), {
    credentials: "include", headers: buildAuthHeaders({ Accept: "application/pdf" }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new Error(payload?.detail || "Le justificatif n’a pas pu être téléchargé.");
  }
  if (!(response.headers.get("Content-Type") || "").startsWith("application/pdf")) throw new Error("Le document reçu n’est pas un PDF.");
  return response.blob();
}

export function prepareBoostBilling(orderId) {
  return request(`/api/monetization/boost-billing/${encodeURIComponent(orderId)}/`, {method: "POST"});
}

export async function downloadBoostBilling(orderId,kind) {
  const response=await fetch(url(`/api/monetization/boost-billing/${encodeURIComponent(orderId)}/${encodeURIComponent(kind)}/`),{
    credentials:"include", headers:buildAuthHeaders({Accept:"application/pdf"})
  });
  if(!response.ok || !response.headers.get("Content-Type")?.includes("application/pdf")) throw new Error("Le document n’a pas pu être téléchargé.");
  return response.blob();
}

export function fetchBoostCheckoutStatus(sessionId, {signal} = {}) {
  return request(`/api/monetization/boost-checkout-status/?session_id=${encodeURIComponent(sessionId)}`, {signal});
}
